import { Deserializer } from 'jsonapi-serializer';
import { APIConnector } from './api/APIConnector';
import { MasterGroup } from '../models/MasterGroup';
import { genericError } from '../models/GenericError';
import { INTERNAL_SERVER_ERROR } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { isSuccessStatus } from "../utilities/requestResponseValidator";

const apiConnector = new APIConnector();
/**
 * Find a specific (should only ever be 1 or none) MasterGroup with masterId and accessGroupId matching.
 * We need this in order to delete the link (accessGroup to master).
 * @param masterId
 * @param accessGroupId
 */
export const getMasterGroup = async (masterId, accessGroupId): Promise<MasterGroup[]> => {
    try {
        const url = 'masterGroup?filter=master.id==' + masterId + ';accessGroup.id==' + accessGroupId + '&include=accessGroup,master';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, results: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const masterGroups: MasterGroup[] = results.map((m): MasterGroup => {
                    return {
                        id: m['id'],
                        accessGroupId: m['access-group']['id'],
                        masterId: m['master']['id']
                    };
                });

                return resolve(masterGroups);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const assignUnassignMasterGroup = async (toBeAssigned: string[], toBeUnassigned: string[], masterId: string) => {
    const results:any = [];
    const newMasterGroups = buildToBeSavedMasterGroup(toBeAssigned, masterId);

    // Handle assignments one at a time
    for (const newMasterGroup of newMasterGroups) {
        try {
            const result = await saveMasterGroup(newMasterGroup, true);
            results.push(result);
        } catch (error) {
            throw error
        }
    }

    const removedAccessGroup = toBeUnassigned.map(async (accessGroupId) => {
        return await deleteMasterGroup(masterId, accessGroupId)
    })

    // Wait for unassignments to complete
    const unassignResults = await Promise.all(removedAccessGroup);

    return [...results, ...unassignResults];
}

const buildToBeSavedMasterGroup = (toBeAssigned: string[], masterId: string) => {
    return toBeAssigned.map((accessGroupId) => {
        return ({
            data: {
                type: 'masterGroup',
                // id: "",
                attributes: {
                    "mgDateTimeInEffect": null,
                    "mgEndDateTimeUtc": null,
                    "mgExpires": 0,
                    "mgStartDateTimeUtc": null
                },
                relationships: {
                    accessGroup: {
                        data: {
                            type: 'accessGroup',
                            id: accessGroupId
                        }
                    },
                    master: {
                        data: {
                            type: 'master',
                            id: masterId
                        }
                    }
                }
            }
        })
    })
}

const buildToBeDeleteMasterGroups = (masterGroup: MasterGroup) => {
    return {
        data: {
            type: 'masterGroup',
            id: masterGroup.id,
            relationships: {
                master: {
                    data: {
                        type: 'master',
                        id: masterGroup.masterId
                    }
                },
                accessGroup: {
                    data: {
                        type: 'accessGroup',
                        id: masterGroup.accessGroupId
                    }
                }
            }
        }
    }
}


/**
 * POST a new MasterGroup which represents the access group assigned to the master.
 * @param masterId
 * @param accessGroup
 */
export const saveMasterGroup = async (masterGroup, isNewMasterGroup = true) => {
    try {
        const url = `masterGroup`;
        let response: Response;

        await apiConnector.sendData(url, isNewMasterGroup, masterGroup).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }

            const json = await response.json();

            return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Delete the MasterGroup for this person and accessGroup.
 * Effectively removes the access group assignment from the person.
 * @param masterId
 * @param accessGroup
 */
export const deleteMasterGroup = async (masterId: string, accessGroupId): Promise<boolean> => {
    try {
        // First lookup the MasterGroup
        const masterGroups = await getMasterGroup(masterId, accessGroupId);

        // Found the MasterGroup, let's delete it then.
        if (masterGroups && masterGroups.length > 0) {
            const masterGroupId = masterGroups[0].id;
            const url = `masterGroup/` + masterGroupId;
            let response: Response;

            await apiConnector.deleteData(url, buildToBeDeleteMasterGroups(masterGroups[0])).then(async (data) => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject({ status: response.status, message: genericError })
                })
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                const json = await response.json();

                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            });
        }
        // Unable to delete the MasterGroup. Couldn't find it.
        else {
            return new Promise<boolean>(async (resolve, reject) => {
                reject({ status: INTERNAL_SERVER_ERROR, message: genericError });
            });
        }
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
